import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySession } from '@/types/optibay';

const sb = supabase as any;

/**
 * Contextual training recommendations based on OptiBay session data.
 * Fetches by category_key then re-ranks using session context:
 * test paths, evidence types, interpretation confidence, verification outcomes.
 */

export interface OptiBayTrainingRecommendation {
  id: string;
  category_key: string;
  recommendation_type: string;
  title: string;
  description: string | null;
  priority: number;
  suggested_artifact_tags: string[] | null;
  _score?: number;
  matchingArtifacts?: Array<{
    id: string;
    title: string;
    artifact_type: string;
    provider_name: string;
  }>;
}

export function useOptiBayTrainingRecommendations(
  session: OptiBaySession | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['optibay-training-recs', session?.id, session?.system_family_id],
    queryFn: async (): Promise<OptiBayTrainingRecommendation[]> => {
      if (!session?.system_family_id || !session?.technician_profile_id) return [];

      // Get the family's category_key
      const { data: family } = await sb
        .from('optibay_system_families')
        .select('category_key')
        .eq('id', session.system_family_id)
        .single();

      if (!family?.category_key) return [];

      // Fetch recommendations + session context in parallel
      const [recsRes, testPathsRes, evidenceRes, interpsRes, verificationsRes] = await Promise.all([
        supabase
          .from('diagnostic_training_recommendations')
          .select('*')
          .eq('technician_profile_id', session.technician_profile_id)
          .eq('category_key', family.category_key)
          .eq('status', 'active')
          .order('priority', { ascending: false })
          .limit(10),
        sb.from('optibay_session_test_paths').select('test_path_id').eq('session_id', session.id),
        sb.from('optibay_session_evidence').select('evidence_type').eq('session_id', session.id),
        sb.from('optibay_session_interpretations').select('confidence').eq('session_id', session.id),
        sb.from('optibay_session_verification_tests').select('passed').eq('session_id', session.id),
      ]);

      const recs = recsRes.data;
      if (!recs || recs.length === 0) return [];

      // Build context sets for scoring
      const evidenceTypes = new Set((evidenceRes.data || []).map((e: any) => e.evidence_type));
      const hasLowConfidence = (interpsRes.data || []).some((i: any) => i.confidence === 'low');
      const hasFailedTests = (verificationsRes.data || []).some((t: any) => t.passed === false);

      // Score and re-rank
      const scored: OptiBayTrainingRecommendation[] = recs.map((rec: any) => {
        let score = rec.priority || 0;

        // Boost if recommendation matches captured evidence types
        if (rec.suggested_artifact_tags?.length) {
          const overlap = rec.suggested_artifact_tags.filter((t: string) => evidenceTypes.has(t));
          score += overlap.length * 2;
        }

        // Boost foundational training when confidence is low
        if (hasLowConfidence && rec.recommendation_type === 'foundational') {
          score += 3;
        }

        // Boost retraining when verification tests failed
        if (hasFailedTests && (rec.recommendation_type === 'retraining' || rec.recommendation_type === 'remediation')) {
          score += 3;
        }

        return { ...rec, _score: score } as OptiBayTrainingRecommendation;
      });

      scored.sort((a, b) => (b._score || 0) - (a._score || 0));
      const top = scored.slice(0, 5);

      // REC-A3: batched artifact hydration replaces the pre-A3 per-rec
      // training_artifacts.contains('keywords', rec.suggested_artifact_tags)
      // fanout. Pre-A3 fired one round trip per top-ranked rec
      // (Promise.all(top.map(async))) — capped at 5 by the upstream
      // `top = scored.slice(0, 5)` slice but still 5 parallel queries
      // per OptiBay session render. Now: ONE batched query that returns
      // the superset of artifacts whose keywords overlap any tag from
      // any top rec, then a per-rec in-memory `.every` filter re-imposes
      // the original ALL-of-tags AND check.
      //
      // Mirrors REC-A2's verbatim pattern in useDiagnosticRecommendations
      // (overlap+filter for Postgres array-CONTAINS semantics — third
      // batching primitive cross-workstream canonical, after Set+IN for
      // FK enrichment and pre-fetched context maps).
      //
      // FIX-R115-HIGH-2 doctrine retained: matches against `keywords`
      // (content tags), not the pre-FIX `teaching_styles` (pedagogy)
      // column. Same column mismatch class as R115-HIGH-1 in
      // useDiagnosticRecommendations.ts; both surfaces were silently
      // returning empty matchingArtifacts before the FIX. The REC-A3
      // batching shape preserves the FIX (`.overlaps('keywords', ...)`
      // still joins against the canonical content-tag column).
      //
      // Per-rec cap preserved at 3 (matches pre-A3 `.limit(3)` — the
      // OptiBay surface intentionally renders fewer artifact links per
      // rec than the diagnostic surface, which renders 5).
      const allTags = [...new Set(
        top.flatMap((r) => r.suggested_artifact_tags || []),
      )];

      let allArtifacts: Array<{
        id: string;
        title: string;
        artifact_type: string;
        provider_name: string;
        keywords: string[] | null;
      }> = [];

      if (allTags.length > 0) {
        const { data } = await supabase
          .from('training_artifacts')
          .select('id, title, artifact_type, provider_name, keywords')
          .eq('is_active', true)
          .overlaps('keywords', allTags);
        allArtifacts = (data || []) as typeof allArtifacts;
      }

      const results: OptiBayTrainingRecommendation[] = top.map((rec) => {
        let matchingArtifacts: OptiBayTrainingRecommendation['matchingArtifacts'] = [];
        if (rec.suggested_artifact_tags?.length) {
          matchingArtifacts = allArtifacts
            .filter((a) =>
              rec.suggested_artifact_tags!.every((t) => a.keywords?.includes(t)),
            )
            .slice(0, 3)
            .map((a) => ({
              id: a.id,
              title: a.title,
              artifact_type: a.artifact_type,
              provider_name: a.provider_name,
            }));
        }
        return { ...rec, matchingArtifacts };
      });

      return results;
    },
    enabled: (options?.enabled ?? true) && !!session?.system_family_id && !!session?.technician_profile_id,
    staleTime: 5 * 60 * 1000,
  });
}
